import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

export type Politeness = "polite" | "assertive";

export type AnnouncerValue = {
  announce: (message: string, politeness?: Politeness) => void;
};

const AnnouncerContext = createContext<AnnouncerValue | null>(null);

/**
 * A single pair of live regions for the whole application.
 *
 * Two regions rather than one so an urgent message (a refusal, a cap being
 * hit) is not queued behind a routine one (a page of results loaded). Messages
 * are re-emitted with a zero-width marker when repeated, because screen
 * readers ignore an identical string written twice.
 */
export function AnnouncerProvider({ children }: { children: ReactNode }) {
  const [polite, setPolite] = useState("");
  const [assertive, setAssertive] = useState("");
  const toggle = useRef(false);

  const announce = useCallback((message: string, politeness: Politeness = "polite") => {
    toggle.current = !toggle.current;
    const payload = toggle.current ? message : `${message}​`;
    if (politeness === "assertive") {
      setAssertive(payload);
    } else {
      setPolite(payload);
    }
  }, []);

  const value = useMemo(() => ({ announce }), [announce]);

  return (
    <AnnouncerContext.Provider value={value}>
      {children}
      {/* Live regions are never recorded by session replay (their text would overlap the page). */}
      <div data-replay-block style={{ display: "contents" }}>
        {/* biome-ignore lint/a11y/useSemanticElements: <output> is scoped to the
          result of a form calculation. These are general-purpose application
          live regions, which is precisely role="status" / role="alert". */}
        <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
          {polite}
        </div>
        <div className="sr-only" role="alert" aria-live="assertive" aria-atomic="true">
          {assertive}
        </div>
      </div>
    </AnnouncerContext.Provider>
  );
}

export function useAnnouncer(): AnnouncerValue {
  const value = useContext(AnnouncerContext);
  // Announcements are an enhancement; a component rendered outside the
  // provider (a unit test, a story) must still work.
  return value ?? { announce: () => undefined };
}
